import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Template Operations
export const getTemplate = async (templateId) => {
  try {
    const docRef = doc(db, 'templates', templateId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    throw error;
  }
};

export const getPublicTemplates = async (realm, selectedClass) => {
  try {
    let q = query(collection(db, 'templates'), where('isPrivate', '==', false));

    if (realm) {
      q = query(q, where('realm', '==', realm));
    }

    if (selectedClass) {
      q = query(q, where('class', '==', selectedClass));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const getSharedTemplates = async (userId) => {
  try {
    // Query for templates where the user is in the sharedUserUids array
    const q = query(
      collection(db, 'templates'),
      where('sharedUserUids', 'array-contains', userId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const getUserTemplates = async (userId) => {
  try {
    const q = query(collection(db, 'templates'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const createTemplate = async (templateData) => {
  try {
    const docRef = await addDoc(collection(db, 'templates'), templateData);
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const updateTemplate = async (templateId, templateData) => {
  try {
    const docRef = doc(db, 'templates', templateId);
    await updateDoc(docRef, templateData);
  } catch (error) {
    throw error;
  }
};

export const deleteTemplate = async (templateId) => {
  try {
    const docRef = doc(db, 'templates', templateId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

// Template Sharing Operations
export const addSharedUser = async (templateId, sharedUser) => {
  try {
    const templateDocRef = doc(db, 'templates', templateId);
    const templateDoc = await getDoc(templateDocRef);
    
    if (!templateDoc.exists()) {
      throw new Error('Template not found');
    }

    const templateData = templateDoc.data();
    const currentSharedUsers = templateData.sharedUsers || [];
    const currentSharedUserUids = templateData.sharedUserUids || [];

    // Check if user is already shared
    if (currentSharedUserUids.includes(sharedUser.uid)) {
      return;
    }

    // Update the template with new shared user
    await updateDoc(templateDocRef, {
      sharedUsers: [...currentSharedUsers, sharedUser],
      sharedUserUids: [...currentSharedUserUids, sharedUser.uid]
    });

    return true;
  } catch (error) {
    throw error;
  }
};

export const removeSharedUser = async (templateId, userId) => {
  try {
    const templateDocRef = doc(db, 'templates', templateId);
    const templateDoc = await getDoc(templateDocRef);
    
    if (!templateDoc.exists()) {
      throw new Error('Template not found');
    }

    const templateData = templateDoc.data();
    const currentSharedUsers = templateData.sharedUsers || [];
    const currentSharedUserUids = templateData.sharedUserUids || [];

    // Remove the user from both arrays
    const updatedSharedUsers = currentSharedUsers.filter(user => user.uid !== userId);
    const updatedSharedUserUids = currentSharedUserUids.filter(uid => uid !== userId);

    // Update the template
    await updateDoc(templateDocRef, {
      sharedUsers: updatedSharedUsers,
      sharedUserUids: updatedSharedUserUids
    });

    return true;
  } catch (error) {
    throw error;
  }
};

export const findUserByDisplayName = async (displayName) => {
  try {
    const usersCollectionRef = collection(db, 'users');
    const q = query(usersCollectionRef, where('displayName', '==', displayName));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return {
        uid: userDoc.id,
        ...userDoc.data()
      };
    }
    return null;
  } catch (error) {
    throw error;
  }
};

// Character Operations
export const getCharacters = async (userId) => {
  try {
    const q = query(collection(db, 'characters'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const createCharacter = async (characterData) => {
  try {
    const docRef = await addDoc(collection(db, 'characters'), characterData);
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const updateCharacter = async (characterId, characterData) => {
  try {
    const docRef = doc(db, 'characters', characterId);
    await updateDoc(docRef, characterData);
  } catch (error) {
    throw error;
  }
};

export const deleteCharacter = async (characterId) => {
  try {
    const docRef = doc(db, 'characters', characterId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

// Guide Operations
export const getGuides = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'guides'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const getGuide = async (guideId) => {
  try {
    const docRef = doc(db, 'guides', guideId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    throw error;
  }
};

export const createGuide = async (guideData) => {
  try {
    const docRef = await addDoc(collection(db, 'guides'), guideData);
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const updateGuide = async (guideId, guideData) => {
  try {
    const docRef = doc(db, 'guides', guideId);
    await updateDoc(docRef, guideData);
  } catch (error) {
    throw error;
  }
};

export const deleteGuide = async (guideId) => {
  try {
    const docRef = doc(db, 'guides', guideId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

// Group Operations
export const getGroups = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'groups'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const createGroup = async (groupData) => {
  try {
    const docRef = await addDoc(collection(db, 'groups'), groupData);
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const updateGroup = async (groupId, groupData) => {
  try {
    const docRef = doc(db, 'groups', groupId);
    await updateDoc(docRef, groupData);
  } catch (error) {
    throw error;
  }
};

export const deleteGroup = async (groupId) => {
  try {
    const docRef = doc(db, 'groups', groupId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

// User Operations
export const getUserData = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    throw error;
  }
};

export const updateUserData = async (userId, userData) => {
  try {
    const docRef = doc(db, 'users', userId);
    await updateDoc(docRef, userData);
  } catch (error) {
    throw error;
  }
}; 